/*
 * Lively Science 统一登录客户端
 *
 * 唯一事实来源：VSTO 插件进程内的本地认证桥（http://localhost:18088）。
 * 侧边栏（VSTO 任务窗格）和幻灯片内容加载项（Content Add-in）都通过它读写
 * 登录状态，因此任意一处登录/退出，另一处都会在 1.5 秒内刷新。
 *
 * 本文件同时被以下两处使用：
 *   - SDKXofficePlugin/src/taskpane     （幻灯片内容加载项，webpack 打包）
 *   - SDKXPowerPointAddIn/taskpane.html （侧边栏，WebView2 直接加载）
 * 修改时请同步到 SDKXPowerPointAddIn/auth-client.js。
 */
(function (global) {
  "use strict";

  var CONFIG = {
    // VSTO 插件中的本地认证桥
    bridgeUrl: "http://localhost:18088",
    apiBase: "https://livelyscience.ca/api",
    tokenStorageKey: "userToken",
    emailStorageKey: "userEmail",
    // 与页面同目录的品牌图标（Figma 3456:12938 中的 Icon-Light）
    logoUrl: "logo.png",
    // 设计图右侧二维码内容。后端接入移动端扫码登录后，改成带 ticket 的地址，
    // 并把 onQrTicket 换成轮询接口即可。
    qrContent: "https://livelyscience.ca/qr-login",
    pollInterval: 1500,
    bridgeTimeout: 1500
  };

  var state = {
    loggedIn: false,
    email: "",
    token: "",
    bridgeOnline: false
  };

  var listeners = [];
  var pollTimer = null;
  var lastSignature = null;
  var adoptTried = false;
  var qrFactory = null;

  // ------------------------------------------------------------------
  // 基础工具
  // ------------------------------------------------------------------
  function cloneState() {
    return {
      loggedIn: state.loggedIn,
      email: state.email,
      token: state.token,
      bridgeOnline: state.bridgeOnline
    };
  }

  function signatureOf(s) {
    return (s.loggedIn ? "1" : "0") + "|" + s.token;
  }

  function notify(change) {
    var snapshot = cloneState();
    listeners.forEach(function (cb) {
      try {
        cb(snapshot, change);
      } catch (e) {
        if (global.console) console.error("[LSAuth] listener error", e);
      }
    });
    try {
      global.dispatchEvent(
        new CustomEvent("lsauthchange", { detail: { state: snapshot, change: change } })
      );
    } catch (e) {
      /* 忽略不支持 CustomEvent 的环境 */
    }
  }

  function fetchWithTimeout(url, options, timeout) {
    return new Promise(function (resolve, reject) {
      var controller = null;
      var timer = null;
      try {
        if (typeof AbortController !== "undefined") controller = new AbortController();
      } catch (e) {
        controller = null;
      }
      var opts = options || {};
      opts.cache = "no-store";
      if (controller) opts.signal = controller.signal;

      timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error("timeout"));
      }, timeout || CONFIG.bridgeTimeout);

      global
        .fetch(url, opts)
        .then(
          function (response) {
            clearTimeout(timer);
            resolve(response);
          },
          function (error) {
            clearTimeout(timer);
            reject(error);
          }
        );
    });
  }

  function readJson(response) {
    if (!response.ok) throw new Error("HTTP " + response.status);
    return response.json();
  }

  function postBridge(path, payload, timeout) {
    return fetchWithTimeout(
      CONFIG.bridgeUrl + path,
      {
        method: "POST",
        headers: { "Content-Type": "application/json;charset=utf-8" },
        body: JSON.stringify(payload || {})
      },
      timeout || 10000
    ).then(readJson);
  }

  function persistLocal(token, email) {
    try {
      if (token) global.localStorage.setItem(CONFIG.tokenStorageKey, token);
      else global.localStorage.removeItem(CONFIG.tokenStorageKey);
      if (email) global.localStorage.setItem(CONFIG.emailStorageKey, email);
      else global.localStorage.removeItem(CONFIG.emailStorageKey);
    } catch (e) {
      /* 隐私模式下 localStorage 可能不可用 */
    }
  }

  function readLocalToken() {
    try {
      return global.localStorage.getItem(CONFIG.tokenStorageKey) || "";
    } catch (e) {
      return "";
    }
  }

  function readLocalEmail() {
    try {
      return global.localStorage.getItem(CONFIG.emailStorageKey) || "";
    } catch (e) {
      return "";
    }
  }

  function applyState(next, change) {
    var wasSignedIn = state.loggedIn;
    state.loggedIn = !!(next && next.loggedIn && next.token);
    state.token = (next && next.token) || "";
    state.email = (next && next.email) || "";
    if (typeof next.bridgeOnline === "boolean") state.bridgeOnline = next.bridgeOnline;

    var first = lastSignature === null;
    var signature = signatureOf(state);
    var changed = !first && signature !== lastSignature;
    lastSignature = signature;

    if (first || changed || wasSignedIn !== state.loggedIn) {
      notify(change || (state.loggedIn ? "login" : "logout"));
    }
    return cloneState();
  }

  function applyLocalFallback() {
    var token = readLocalToken();
    if (token) {
      return applyState({
        loggedIn: true,
        token: token,
        email: readLocalEmail(),
        bridgeOnline: false
      });
    }
    return applyState({ loggedIn: false, token: "", email: "", bridgeOnline: false });
  }

  // ------------------------------------------------------------------
  // 状态读取 / 登录 / 退出
  // ------------------------------------------------------------------
  function refresh() {
    return fetchWithTimeout(
      CONFIG.bridgeUrl + "/auth/state?ts=" + Date.now(),
      { method: "GET" },
      CONFIG.bridgeTimeout
    )
      .then(readJson)
      .then(function (data) {
        state.bridgeOnline = true;
        var snapshot = {
          loggedIn: data.loggedIn,
          token: data.token || "",
          email: data.email || "",
          bridgeOnline: true
        };

        // 首次连接时把旧版本留在 localStorage 里的 token 迁移进中央存储
        if (!snapshot.loggedIn && !adoptTried) {
          adoptTried = true;
          var legacyToken = readLocalToken();
          if (legacyToken) {
            return postBridge(
              "/auth/adopt",
              { token: legacyToken, email: readLocalEmail() },
              3000
            )
              .then(function (adopted) {
                if (adopted && adopted.loggedIn) {
                  persistLocal(adopted.token, adopted.email);
                  return applyState(adopted, "login");
                }
                persistLocal("", "");
                return applyState(snapshot);
              })
              .catch(function () {
                return applyState(snapshot);
              });
          }
        }

        if (snapshot.loggedIn) persistLocal(snapshot.token, snapshot.email);
        else persistLocal("", "");
        return applyState(snapshot);
      })
      .catch(function () {
        state.bridgeOnline = false;
        return applyLocalFallback();
      });
  }

  function directLogin(email, password) {
    return global
      .fetch(CONFIG.apiBase + "/app/portal/email-login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json;charset=utf-8",
          osType: "10",
          appVersion: "1.0.0",
          source: "Android"
        },
        body: JSON.stringify({ email: email, password: password })
      })
      .then(function (response) {
        if (!response.ok) throw new Error("HTTP " + response.status);
        return response.json();
      })
      .then(function (data) {
        if (data && (data.status === true || data.status === "true") && data.data) {
          var token = data.data.token;
          persistLocal(token, email);
          // 桥恢复后把 token 交给中央存储；失败不影响本次登录
          postBridge("/auth/adopt", { token: token, email: email }, 3000).catch(
            function () {}
          );
          return { ok: true, state: applyState({ loggedIn: true, token: token, email: email }, "login") };
        }
        return { ok: false, msg: (data && data.msg) || "账号或密码错误" };
      })
      .catch(function (error) {
        return { ok: false, msg: "网络请求失败：" + (error && error.message ? error.message : "未知错误") };
      });
  }

  function login(email, password) {
    var trimmedEmail = (email || "").trim();
    if (!trimmedEmail || !(password || "").trim()) {
      return Promise.resolve({ ok: false, msg: "请输入邮箱和密码" });
    }

    return postBridge(
      "/auth/login",
      { email: trimmedEmail, password: password },
      20000
    )
      .then(function (result) {
        if (result && result.ok && result.loggedIn) {
          persistLocal(result.token, result.email);
          state.bridgeOnline = true;
          return { ok: true, state: applyState(result, "login") };
        }
        return { ok: false, msg: (result && result.msg) || "登录失败，请稍后再试" };
      })
      .catch(function (error) {
        // 桥不可用（VSTO 未运行）时，退化为直接请求业务接口
        state.bridgeOnline = false;
        if (!error || error.message === "timeout") {
          return directLogin(trimmedEmail, password);
        }
        return directLogin(trimmedEmail, password);
      });
  }

  function logout() {
    var finish = function () {
      persistLocal("", "");
      return applyState({ loggedIn: false, token: "", email: "" }, "logout");
    };
    return postBridge("/auth/logout", {}, 3000)
      .catch(function () {
        return null;
      })
      .then(finish);
  }

  /**
   * 请求 VSTO 宿主打开与侧边栏相同的登录窗口。
   * 供幻灯片内容加载项使用；VSTO 未运行时 Promise 会 reject。
   */
  function requestHostLogin() {
    return postBridge("/auth/open-login", {}, 3000);
  }

  function getState() {
    return cloneState();
  }

  function getToken() {
    return state.token;
  }

  function onChange(callback) {
    if (typeof callback === "function") {
      listeners.push(callback);
      callback(cloneState(), "initial");
    }
  }

  function startWatching() {
    if (pollTimer) return;
    pollTimer = setInterval(function () {
      if (typeof document !== "undefined" && document.hidden) return;
      refresh().catch(function () {});
    }, CONFIG.pollInterval);

    try {
      global.addEventListener("storage", function (event) {
        if (
          event.key === CONFIG.tokenStorageKey ||
          event.key === CONFIG.emailStorageKey
        ) {
          refresh().catch(function () {});
        }
      });
    } catch (e) {
      /* 忽略 */
    }
  }

  function stopWatching() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  // ------------------------------------------------------------------
  // 登录弹窗（设计图）
  // ------------------------------------------------------------------
  var LOGO_IMG =
    '<img class="ls-signin-logo-img" src="' + CONFIG.logoUrl + '" alt="" />';

  var MODAL_CSS =
    ".ls-signin-overlay{position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(0,0,0,.32);" +
    "display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;" +
    "z-index:2147483000;font-family:'Segoe UI','Microsoft YaHei',Tahoma,sans-serif;-webkit-font-smoothing:antialiased;}" +
    ".ls-signin-card,.ls-signin-card *{box-sizing:border-box;}" +
    ".ls-signin-card{position:relative;display:flex;width:507px;height:338px;max-width:100%;background:#F2F2F7;" +
    "border:1px solid #E3E3E3;border-radius:21px;box-shadow:0 18px 44px rgba(0,0,0,.14);overflow:hidden;" +
    "animation:ls-signin-pop .16s ease-out;}" +
    "@keyframes ls-signin-pop{from{opacity:0;transform:translateY(6px) scale(.985);}to{opacity:1;transform:none;}}" +
    ".ls-signin-left{flex:0 0 50%;background:#F2F2F7;padding:38px 30px 0;display:flex;flex-direction:column;}" +
    ".ls-signin-logo{display:flex;justify-content:center;}" +
    ".ls-signin-logo-img{display:block;width:44px;height:44px;border-radius:50%;background:#fff;object-fit:contain;}" +
    ".ls-signin-title{margin:7px 0 21px;font-family:'Segoe UI','Microsoft YaHei',sans-serif;font-size:18px;line-height:27px;" +
    "font-weight:700;letter-spacing:.26px;color:#000;text-align:center;}" +
    ".ls-signin-subtitle{margin:-12px 0 12px;font-size:11px;line-height:15px;color:#515151;text-align:center;}" +
    ".ls-signin-input{display:block;width:100%;height:36px;padding:0 14px;margin:0 0 15px;" +
    "border:1px solid #EDEDF2;border-radius:18px;background:#fff;font-size:13px;color:#262626;outline:none;" +
    "font-family:'Segoe UI','Microsoft YaHei',sans-serif;transition:border-color .15s,box-shadow .15s;}" +
    ".ls-signin-input::placeholder{color:#B4B4B4;}" +
    ".ls-signin-input:focus{border-color:#4285F4;box-shadow:0 0 0 2px rgba(66,133,244,.15);}" +
    ".ls-signin-error{margin:0 4px;font-size:11px;line-height:15px;color:#D92D20;}" +
    ".ls-signin-error:empty{display:none;}" +
    ".ls-signin-submit{width:100%;height:36px;margin-top:35px;border:none;border-radius:60px;background:#4285F4;" +
    "color:#fff;font-size:13px;font-weight:700;font-family:'Segoe UI','Microsoft YaHei',sans-serif;cursor:pointer;" +
    "transition:background .15s;}" +
    ".ls-signin-submit:hover{background:#3B78E7;}" +
    ".ls-signin-submit:disabled{opacity:.65;cursor:default;}" +
    ".ls-signin-right{flex:0 0 50%;background:#fff;display:flex;flex-direction:column;align-items:center;" +
    "padding-top:68px;position:relative;}" +
    ".ls-signin-close{position:absolute;top:15px;right:15px;width:24px;height:24px;padding:0;border:none;border-radius:50%;" +
    "background:#F2F2F7;color:#7A7A7A;font-size:12px;line-height:1;cursor:pointer;display:flex;align-items:center;" +
    "justify-content:center;font-family:'Segoe UI',sans-serif;transition:background .15s,color .15s;}" +
    ".ls-signin-close:hover{background:#E8E8EE;color:#4B4B4B;}" +
    ".ls-signin-qr{width:180px;height:180px;padding:4px;display:flex;align-items:center;justify-content:center;}" +
    ".ls-signin-qr svg{width:100%;height:100%;display:block;}" +
    ".ls-signin-qr-fallback{font-size:11px;color:#9CA3AF;text-align:center;line-height:1.4;}" +
    ".ls-signin-qr-caption{width:150px;margin-top:18px;font-family:'Microsoft YaHei','Segoe UI',sans-serif;font-size:10px;" +
    "line-height:13.2px;color:#515151;text-align:center;}" +
    ".ls-signin-overlay--embedded{background:transparent;padding:0;overflow:hidden;}" +
    ".ls-signin-overlay--embedded .ls-signin-card{width:507px;height:338px;max-width:none;border-radius:21px;box-shadow:none;animation:none;}" +
    "@media (max-width:520px){.ls-signin-right{display:none;}.ls-signin-card{height:auto;min-height:0;}" +
    ".ls-signin-left{flex:1 1 auto;padding-bottom:30px;}}" +
    ".ls-signin-overlay--embedded .ls-signin-right{display:flex;}" +
    "@media (max-height:380px){.ls-signin-card{height:auto;}.ls-signin-right{padding-top:48px;}}";

  function injectStyles() {
    if (typeof document === "undefined") return;
    if (document.getElementById("ls-signin-style")) return;
    var style = document.createElement("style");
    style.id = "ls-signin-style";
    style.type = "text/css";
    style.appendChild(document.createTextNode(MODAL_CSS));
    (document.head || document.documentElement).appendChild(style);
  }

  function renderQr(container) {
    var factory = qrFactory || global.qrcode;
    if (typeof factory !== "function" || !CONFIG.qrContent) {
      container.innerHTML =
        '<div class="ls-signin-qr-fallback">扫码登录<br/>暂不可用</div>';
      return;
    }
    try {
      var qr = factory(0, "M");
      qr.addData(CONFIG.qrContent);
      qr.make();
      container.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
      var svg = container.querySelector("svg");
      if (svg) {
        svg.setAttribute("width", "100%");
        svg.setAttribute("height", "100%");
      }
    } catch (e) {
      container.innerHTML =
        '<div class="ls-signin-qr-fallback">二维码<br/>生成失败</div>';
    }
  }

  /**
   * 打开设计图中的登录弹窗。
   * options:
   *   subtitle    标题下方的一行说明（可选）
   *   embedded    true 时铺满容器（用于独立的 WebView2 登录窗口）
   *   closable    是否显示右上角关闭按钮，默认 true
   *   onSuccess   登录成功回调 (state)
   *   onClose     关闭回调
   *   onHostClose 自定义关闭行为（例如向 VSTO 发送关闭消息）
   */
  function openModal(options) {
    var opts = options || {};
    injectStyles();

    var previous = document.getElementById("ls-signin-root");
    if (previous && previous.parentNode) previous.parentNode.removeChild(previous);

    var root = document.createElement("div");
    root.id = "ls-signin-root";
    root.className =
      "ls-signin-overlay" + (opts.embedded ? " ls-signin-overlay--embedded" : "");

    root.innerHTML =
      '<div class="ls-signin-card" role="dialog" aria-modal="true" aria-label="Sign in to Lively Science">' +
      '<div class="ls-signin-left">' +
      '<div class="ls-signin-logo">' + LOGO_IMG + "</div>" +
      '<div class="ls-signin-title">Sign in</div>' +
      (opts.subtitle ? '<div class="ls-signin-subtitle">' + escapeHtml(opts.subtitle) + "</div>" : "") +
      '<input type="email" class="ls-signin-input" id="ls-signin-email" placeholder="Enter Email" autocomplete="username" />' +
      '<input type="password" class="ls-signin-input" id="ls-signin-password" placeholder="Enter password" autocomplete="current-password" />' +
      '<div class="ls-signin-error" id="ls-signin-error" role="alert"></div>' +
      '<button type="button" class="ls-signin-submit" id="ls-signin-submit">Sign In</button>' +
      "</div>" +
      '<div class="ls-signin-right">' +
      (opts.closable === false
        ? ""
        : '<button type="button" class="ls-signin-close" id="ls-signin-close" aria-label="Close">&#10005;</button>') +
      '<div class="ls-signin-qr" id="ls-signin-qr"></div>' +
      '<div class="ls-signin-qr-caption">Scan with the Lively Science app to sign in.</div>' +
      "</div>" +
      "</div>";

    (document.body || document.documentElement).appendChild(root);
    renderQr(root.querySelector("#ls-signin-qr"));

    // 独立登录窗口：如果 WebView 的 CSS 视口小于设计稿尺寸，
    // 整体等比缩小，保证 507×338 的卡片在任何 DPI 下都完整可见。
    var embeddedResize = null;
    if (opts.embedded) {
      var cardElement = root.querySelector(".ls-signin-card");
      embeddedResize = function () {
        var width = root.clientWidth || window.innerWidth;
        var height = root.clientHeight || window.innerHeight;
        var scale = Math.min(width / 507, height / 338, 1);
        cardElement.style.transformOrigin = "center center";
        cardElement.style.transform = scale < 0.999 ? "scale(" + scale + ")" : "none";
      };
      embeddedResize();
      window.addEventListener("resize", embeddedResize);
    }

    var emailInput = root.querySelector("#ls-signin-email");
    var passwordInput = root.querySelector("#ls-signin-password");
    var errorBox = root.querySelector("#ls-signin-error");
    var submitBtn = root.querySelector("#ls-signin-submit");
    var closeBtn = root.querySelector("#ls-signin-close");
    var closed = false;

    function close() {
      if (closed) return;
      closed = true;
      if (embeddedResize) window.removeEventListener("resize", embeddedResize);
      if (root.parentNode) root.parentNode.removeChild(root);
      if (typeof opts.onClose === "function") opts.onClose();
    }

    function hostClose() {
      if (typeof opts.onHostClose === "function") opts.onHostClose();
      else close();
    }

    function showError(message) {
      errorBox.textContent = message || "";
    }

    function submit() {
      var email = emailInput.value;
      var password = passwordInput.value;
      if (!email.trim() || !password.trim()) {
        showError("Please enter your email and password.");
        return;
      }
      showError("");
      submitBtn.disabled = true;
      submitBtn.textContent = "Signing in…";

      login(email, password).then(function (result) {
        if (closed) return;
        if (result && result.ok) {
          close();
          if (typeof opts.onSuccess === "function") opts.onSuccess(result.state);
        } else {
          submitBtn.disabled = false;
          submitBtn.textContent = "Sign In";
          showError((result && result.msg) || "Sign in failed. Please try again.");
        }
      });
    }

    submitBtn.addEventListener("click", submit);
    emailInput.addEventListener("keydown", function (e) {
      if (e.key === "Enter") submit();
    });
    passwordInput.addEventListener("keydown", function (e) {
      if (e.key === "Enter") submit();
    });
    if (closeBtn) closeBtn.addEventListener("click", hostClose);

    setTimeout(function () {
      emailInput.focus();
    }, 30);

    return {
      close: close,
      element: root
    };
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  global.LSAuth = {
    config: CONFIG,
    init: refresh,
    refresh: refresh,
    login: login,
    logout: logout,
    requestHostLogin: requestHostLogin,
    getState: getState,
    getToken: getToken,
    onChange: onChange,
    startWatching: startWatching,
    stopWatching: stopWatching,
    openModal: openModal,
    setQrFactory: function (factory) {
      qrFactory = factory;
    }
  };
})(typeof window !== "undefined" ? window : this);
